import { registerComponent } from "helpers";
import { refreshCartAfterOffersPayload } from "cart-discount-refresh";
import {
  buildOffersUrl,
  isEthelErpIdPresent,
  isRewardsUserConsented,
  normalizeOffers,
  REWARDS_USER_STATUS_ATTRIBUTE,
  shouldFetchOffers,
} from "rewards-offers-utils";
import WYATTElement from "wyatt";

registerComponent(
  "rewards-offers",
  class RewardsOffers extends WYATTElement {
    #pollStartTime = 0;
    #nextPollDelayMs = 0;
    #pollTimeoutId = null;
    #hasDisplayedOffers = false;
    /** @type {"login-for-checkout" | "sync" | null} */
    #gateBanner = null;

    constructor() {
      super();

      this.offersRoot = this.querySelector("[data-rewards-root]");

      if (!this.canRequestOffers()) {
        this.renderLogin();
        return;
      }

      if (this.isShopifyLoggedIn && !this.isEthelSynced) {
        if (!this.hasPreviewQueryParams()) {
          this.renderSync();
          return;
        }

        this.#gateBanner = "sync";
      }

      this.setBusy(true);

      if (!this.seedFromMetafieldsIfReady()) {
        this.renderActivating();
      }

      this.fetchOffers().catch(() => {});
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.clearActivatePollTimeout();
    }

    get isShopifyLoggedIn() {
      return this.config.customerId != null;
    }

    get isEthelSynced() {
      return isEthelErpIdPresent(this.config.erpId);
    }

    get existingPromotions() {
      return WYATT.shopify?.customer?.metafields?.ethel?.promotions;
    }

    hasPreviewQueryParams() {
      const searchParams = new URLSearchParams(window.location.search);

      return Boolean(
        searchParams.get("customerId") && searchParams.get("token"),
      );
    }

    canRequestOffers() {
      return this.isShopifyLoggedIn || this.hasPreviewQueryParams();
    }

    expectsRefresh() {
      return shouldFetchOffers(this.existingPromotions);
    }

    /**
     * Choose the guest preview gate from the proxy `user_status`.
     * Defaults to the sync/profile CTA unless the shopper is already found + consented.
     * Persists status on the cart so cart/mini-cart can route login vs profile.
     * @param {unknown} userStatus
     */
    applyPreviewUserStatus(userStatus) {
      if (this.isShopifyLoggedIn || !this.hasPreviewQueryParams()) {
        return;
      }

      this.#gateBanner = isRewardsUserConsented(userStatus)
        ? "login-for-checkout"
        : "sync";

      this.persistPreviewUserStatus(userStatus).catch(() => {});
    }

    /**
     * Stamp `user_status` onto the cart for Liquid cart/mini-cart gates.
     * @param {unknown} userStatus
     * @returns {Promise<void>}
     */
    async persistPreviewUserStatus(userStatus) {
      if (userStatus == null || String(userStatus).trim() === "") {
        return;
      }

      const status = String(userStatus);

      await WYATT.Cart.ready;

      if (
        WYATT.Cart.data?.attributes?.[REWARDS_USER_STATUS_ATTRIBUTE] === status
      ) {
        return;
      }

      const result = await WYATT.Cart.update({
        attributes: {
          [REWARDS_USER_STATUS_ATTRIBUTE]: status,
        },
        sections: WYATT.Cart.getSectionNames(),
        sections_url: window.location.pathname,
      });

      if (result?.sections) {
        this.publish(
          WYATT.PubSub.events.updateDOM,
          WYATT.Cart.sectionUpdates(result.sections),
        );
      }
    }

    getOffersAuthParams() {
      if (this.isShopifyLoggedIn && this.isEthelSynced) {
        return { loggedInCustomerId: this.config.customerId };
      }

      const searchParams = new URLSearchParams(window.location.search);

      return {
        customerId: searchParams.get("customerId"),
        token: searchParams.get("token"),
      };
    }

    get activatePollConfig() {
      return {
        initialDelayMs: this.config.activatePollInitialDelayMs ?? 5000,
        maxDurationMs: this.config.activatePollMaxDurationMs ?? 120_000,
        backoffMultiplier: this.config.activatePollBackoffMultiplier ?? 2,
      };
    }

    setBusy(isBusy) {
      this.offersRoot?.setAttribute("aria-busy", isBusy ? "true" : "false");
    }

    seedFromMetafieldsIfReady() {
      const existing = normalizeOffers(this.existingPromotions);

      if (existing.length === 0 || shouldFetchOffers(existing)) {
        return false;
      }

      this.renderFromPayload({ offers: existing }, { announce: false });
      return true;
    }

    clearActivatePollTimeout() {
      if (this.#pollTimeoutId != null) {
        clearTimeout(this.#pollTimeoutId);
        this.#pollTimeoutId = null;
      }
    }

    resetActivatePoll() {
      this.clearActivatePollTimeout();
      this.#pollStartTime = 0;
      this.#nextPollDelayMs = 0;
    }

    startPollingIfNeeded() {
      const { initialDelayMs } = this.activatePollConfig;
      this.#pollStartTime = Date.now();
      this.#nextPollDelayMs = initialDelayMs;
    }

    continueWaitingForOffers() {
      if (this.#pollStartTime === 0) {
        this.startPollingIfNeeded();
      }

      if (!this.#hasDisplayedOffers) {
        this.renderActivating();
      }

      this.setBusy(true);
      this.scheduleActivateOffersRetry();
    }

    shouldTreatEmptyAsPending(data) {
      if (normalizeOffers(data?.offers).length > 0) {
        return false;
      }

      if (data?.status === "loaded") {
        return false;
      }

      if (this.#pollStartTime > 0) {
        return false;
      }

      return this.expectsRefresh();
    }

    scheduleActivateOffersRetry() {
      this.clearActivatePollTimeout();

      const { maxDurationMs, backoffMultiplier } = this.activatePollConfig;
      const elapsed = Date.now() - this.#pollStartTime;

      if (elapsed >= maxDurationMs) {
        this.showUnableToFetch();
        return;
      }

      const remaining = maxDurationMs - elapsed;
      const delay = Math.min(this.#nextPollDelayMs, remaining);
      this.#nextPollDelayMs *= backoffMultiplier;

      this.#pollTimeoutId = window.setTimeout(() => {
        this.#pollTimeoutId = null;
        this.fetchOffers().catch(() => {});
      }, delay);
    }

    async fetchOffers() {
      let response;

      const url = buildOffersUrl({
        ...this.getOffersAuthParams(),
        refresh: this.#pollStartTime === 0 && this.expectsRefresh(),
      });

      try {
        response = await fetch(url);
      } catch {
        this.handleFetchFailure();
        return;
      }

      let payload;

      try {
        payload = await response.json();
      } catch {
        this.renderErrorMessage();
        return;
      }

      if (response.status !== 200) {
        this.renderErrorMessage();
        return;
      }

      this.handleSuccessPayload(payload);
    }

    handleFetchFailure() {
      if (this.#pollStartTime > 0) {
        this.scheduleActivateOffersRetry();
        return;
      }

      this.renderErrorMessage();
    }

    handleSuccessPayload(data) {
      this.applyPreviewUserStatus(data?.user_status);

      if (data?.status === "activating") {
        this.continueWaitingForOffers();
        return;
      }

      if (this.shouldTreatEmptyAsPending(data)) {
        this.continueWaitingForOffers();
        return;
      }

      this.resetActivatePoll();
      this.renderFromPayload(data);

      if (this.isShopifyLoggedIn && this.isEthelSynced) {
        refreshCartAfterOffersPayload(data).catch(() => {});
      }
    }

    showUnableToFetch() {
      if (this.#hasDisplayedOffers) {
        return;
      }

      this.renderNoOffers();
    }

    cloneTemplate(id) {
      return this.querySelector(`#${id}`).content.cloneNode(true);
    }

    gateBannerTemplateId() {
      if (this.#gateBanner === "sync") {
        return "rewards-offers-template-sync";
      }

      if (this.#gateBanner === "login-for-checkout") {
        return "rewards-offers-template-login-for-checkout";
      }

      return null;
    }

    /**
     * @param {...Node} nodes
     */
    replaceRootContent(...nodes) {
      this.offersRoot.replaceChildren();

      const bannerId = this.gateBannerTemplateId();

      if (bannerId) {
        this.offersRoot.append(this.cloneTemplate(bannerId));
      }

      this.offersRoot.append(...nodes);
    }

    renderTemplate(templateId, { busy = false } = {}) {
      this.#hasDisplayedOffers = false;
      this.replaceRootContent(this.cloneTemplate(templateId));
      this.setBusy(busy);
    }

    renderLogin() {
      this.#gateBanner = null;
      this.renderTemplate("rewards-offers-template-login");
    }

    renderSync() {
      this.#gateBanner = null;
      this.#hasDisplayedOffers = false;
      this.replaceRootContent(
        this.cloneTemplate("rewards-offers-template-no-offers"),
        this.cloneTemplate("rewards-offers-template-sync"),
      );
      this.setBusy(false);
    }

    renderErrorMessage() {
      this.resetActivatePoll();
      this.renderTemplate("rewards-offers-template-unable");
    }

    renderNoOffers() {
      this.resetActivatePoll();
      this.renderTemplate("rewards-offers-template-no-offers");
    }

    renderActivating() {
      this.renderTemplate("rewards-offers-template-activating", { busy: true });
    }

    interpolate(template, vars) {
      let out = template;

      if (vars.count != null) {
        out = out.replaceAll("__COUNT__", String(vars.count));
      }

      return out;
    }

    renderOffer(offer) {
      const fragment = this.cloneTemplate("rewards-offers-template-offer");
      const locale = this.config.locale || "en";
      const imageKey = `image_link_${locale}`;
      const imageUrl = offer[imageKey];
      const copy = offer[locale] || {};
      const title = copy.title || "";
      const saveDescription = copy.save_description_1 || "";
      const expiry = offer.valid_until || "";
      const descriptions = [
        copy.offer_description_1,
        copy.offer_description_2,
        copy.offer_description_3,
      ].filter(Boolean);

      const img = fragment.querySelector("[data-offer-image]");
      img.src = imageUrl || "";
      img.alt = title;

      fragment.querySelector("[data-offer-save]").textContent = saveDescription;
      fragment.querySelector("[data-offer-title]").textContent = title;

      const descContainer = fragment.querySelector("[data-offer-descriptions]");
      descContainer.replaceChildren();

      for (const text of descriptions) {
        const span = document.createElement("span");
        span.className = "no-margin";
        span.textContent = text;
        descContainer.append(span);
      }

      const expiryEl = fragment.querySelector("[data-offer-expiry]");

      if (expiry && !offer.redeemed) {
        const expiryDate = new Date(expiry);
        const formattedDate = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(expiryDate);
        expiryEl.textContent = `${this.config.expiresLabel} ${formattedDate}`;
        expiryEl.hidden = false;
      }

      const skus = Array.isArray(offer.skus) ? offer.skus.join("-") : "";
      const detailsLink = fragment.querySelector("[data-offer-details]");

      detailsLink.href = `${WYATT.routes.root}${WYATT.proxy_url.url}/${WYATT.proxy_url.identifier}/plus/${skus}`;

      const analytics = fragment.querySelector("wyatt-analytics");

      if (analytics) {
        analytics.payload = offer;
      }

      return fragment;
    }

    renderOffersSection(title, sectionOffers) {
      const h2 = document.createElement("h2");
      h2.className = "rewards-offers__heading heading-3";
      h2.textContent = title;

      const wrap = document.createElement("div");
      wrap.className = "rewards-offers__offers";

      const list = document.createElement("div");
      list.className = "rewards-offers__list";

      for (const offer of sectionOffers) {
        list.append(this.renderOffer(offer));
      }

      wrap.append(list);

      return [h2, wrap];
    }

    renderRedeemedSection(title, sectionOffers) {
      const fragment = this.cloneTemplate("rewards-offers-template-redeemed");

      fragment.querySelector("[data-redeemed-title]").textContent = title;

      const list = fragment.querySelector("[data-redeemed-list]");

      for (const offer of sectionOffers) {
        list.append(this.renderOffer(offer));
      }

      return fragment;
    }

    renderFromPayload(data, { announce = true } = {}) {
      const offers = normalizeOffers(data?.offers);
      this.#hasDisplayedOffers = false;
      this.setBusy(false);

      if (offers.length === 0) {
        this.replaceRootContent(
          this.cloneTemplate("rewards-offers-template-no-offers"),
        );

        return;
      }

      const activeOffers = offers.filter((offer) => !offer.redeemed);
      const redeemedOffers = offers.filter((offer) => offer.redeemed);

      const sections = [
        ...this.renderOffersSection(
          this.interpolate(this.config.titleWithCount, {
            count: activeOffers.length,
          }),
          activeOffers,
        ),
      ];

      if (redeemedOffers.length > 0) {
        sections.push(
          this.renderRedeemedSection(
            this.config.redeemedOffers,
            redeemedOffers,
          ),
        );
      }

      this.#hasDisplayedOffers = true;
      this.replaceRootContent(...sections);

      if (!announce) {
        return;
      }

      WYATT.Analytics.publish(WYATT.Analytics.events.view_offer_list, {
        offers,
      });
    }
  },
);
